import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { authenticateWithPinCode } from '../firebase/userManagement';

// Define user data interface
export interface AppUser {
  uid: string;
  email: string;
  displayName?: string;
  role: 'admin' | 'staff' | 'user';
  pinCode?: string; // 6-digit PIN for quick access
  staffId?: string; // For staff members
  allowedHostings?: string[]; // Array of hostings this user can access
}

// Define the context type
interface AuthContextType {
  currentUser: User | null;
  userData: AppUser | null;
  loading: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  loginWithPinCode: (pinCode: string) => Promise<AppUser | null>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userData: null,
  loading: true,
  isAdmin: false,
  isStaff: false,
  loginWithPinCode: async () => null,
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Auth provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Function to login with PIN code
  const loginWithPinCode = async (pinCode: string): Promise<AppUser | null> => {
    try {
      // Find user with matching PIN code
      const userWithPin = await authenticateWithPinCode(pinCode);
      
      if (userWithPin) {
        // If we found a user with this PIN, sign in with their email
        // Note: We need to know their password or have them already logged in
        // This is a security consideration
        
        // For now, return the user data so the app can handle PIN-based authentication
        // without actual Firebase Auth login
        return userWithPin;
      }
      
      return null;
    } catch (error) {
      console.error('Error logging in with PIN:', error);
      return null;
    }
  };

  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      setCurrentUser(user);
      
      // If user is logged in, fetch their data from Firestore
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (userDoc.exists()) {
            const userData = userDoc.data() as AppUser;
            setUserData(userData);
          } else {
            // User document doesn't exist in Firestore
            console.warn('User document not found in Firestore for UID:', user.uid);
            setUserData({
              uid: user.uid,
              email: user.email || '',
              role: 'user', // Default role
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserData({
            uid: user.uid,
            email: user.email || '',
            role: 'user', // Default role
          });
        }
      } else {
        // User is logged out
        setUserData(null);
      }
      
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    loading,
    isAdmin: userData?.role === 'admin',
    isStaff: userData?.role === 'admin' || userData?.role === 'staff',
    loginWithPinCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 